import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';
import React from 'react';

interface WordLimitWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  wordCount: number;
  wordLimit: number;
}

export const WordLimitWarningModal = React.memo(function WordLimitWarningModal({
  isOpen,
  onClose,
  wordCount,
  wordLimit,
}: WordLimitWarningModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          className="relative bg-gradient-to-br from-[#0a0a0a] via-[#0f1419] to-[#080808] border border-[#2a2f35] rounded-xl p-6 shadow-[0_0_40px_rgba(79,70,229,0.15)] max-w-md mx-4"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.2 }}
        >
          {/* Background Blur Orbs - Optimized */}
          <div
            className="absolute top-0 left-0 w-32 h-32 bg-[#ef4444] opacity-10 rounded-full blur-[100px] transform -translate-x-1/2 -translate-y-1/2"
            style={{ willChange: 'transform' }}
          />
          <div
            className="absolute bottom-0 right-0 w-24 h-24 bg-[#f59e0b] opacity-10 rounded-full blur-[80px] transform translate-x-1/2 translate-y-1/2"
            style={{ willChange: 'transform' }}
          />

          {/* Content */}
          <div className="relative z-10">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#ef4444]/20 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-[#ef4444]" />
                </div>
                <h2 className="text-lg font-bold text-white">Pre-Choice Text Too Long</h2>
              </div>
              <button
                onClick={onClose}
                className="p-1 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Message */}
            <div className="mb-6">
              <p className="text-gray-300 mb-3">
                Your text is currently{' '}
                <span className="font-bold text-[#ef4444]">{wordCount} words</span>, which exceeds
                the <span className="font-bold text-[#06b6d4]">{wordLimit} word limit</span>.
              </p>
              <p className="text-gray-400 text-sm">
                Please reduce your text to below {wordLimit} words before navigating to another
                node.
              </p>
            </div>

            {/* Action */}
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] text-white font-medium rounded-lg hover:shadow-[0_0_20px_rgba(79,70,229,0.3)] transition-all duration-300"
              >
                Go Back to Edit
              </button>
            </div>
          </div>

          {/* Bottom Gradient Accent */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#ef4444] to-[#f59e0b] opacity-50" />
        </motion.div>
      </div>
    </AnimatePresence>
  );
});
